import Link from "next/link";
import { Logo } from "@/components/Logo";
import { getFaq } from "@/content/registry";
import { t } from "@/content/ui";
import { localeHome, localePath, LOCALE_TAG, type Locale } from "@/lib/i18n";
import {
  CONTACT_EMAIL,
  SITE_NAME,
  SITE_URL,
  activeSocials,
  whatsappUrl,
} from "@/lib/site";

/**
 * THE site footer — one implementation, used by the landing page and by every public page through
 * PublicShell.
 *
 * It replaces two near-identical footers that had already started to diverge (the landing page's
 * carried a "Get started" button, the public shell's did not, and only one linked the FAQ). That
 * is the failure this repo keeps paying for, so there is one now and it lives here.
 *
 * NOT "use client" — the public pages are server-rendered so their content reaches crawlers that
 * never execute JavaScript, and a footer full of internal links is a real part of that: the deep
 * links into /faq anchors below are how an engine discovers that a specific answer exists. The
 * landing page IS a client component, and importing this from there simply compiles it into that
 * bundle; nothing here uses a server-only API, so both work.
 *
 * ⚠️ EVERY EXTERNAL LINK IS OPTIONAL AND HIDDEN WHEN UNSET (lib/site.ts). A social icon pointing
 * at a handle nobody owns is worse than no icon — and because these same URLs are emitted as
 * `sameAs` on the Organization JSON-LD, a wrong one does not merely 404, it ASSERTS that someone
 * else's account is twoke.
 *
 * The Terms and Privacy links were ABSENT here until 2026-08-15, because neither page existed —
 * /legal/terms 404'd while config/tos.ts and TosBanner.tsx already pointed at it. Both are real
 * routes now, so the links are real too. Keep that order: a footer link to a 404 is the one thing
 * a footer must not do, and it is a trust signal crawlers check.
 *
 */

const IconX = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="currentColor"
    className="social-mark"
    aria-hidden
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const IconFacebook = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="currentColor"
    className="social-mark"
    aria-hidden
  >
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.5-3.91 3.77-3.91 1.09 0 2.23.2 2.23.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.45-4.92 8.45-9.94z" />
  </svg>
);

const IconWhatsApp = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="currentColor"
    className="social-mark"
    aria-hidden
  >
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.41a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23z" />
  </svg>
);

const SOCIAL_ICON: Record<string, () => React.JSX.Element> = {
  X: IconX,
  Facebook: IconFacebook,
};

/** Each network's own brand colour (owner 2026-08-22: "show the logo in the right color").
 *  Facebook's blue and WhatsApp's green are their published brand values; X's mark is black on
 *  light and white on dark by its own guidelines, which is exactly the theme's primary text
 *  token. Hover keeps the site's yellow so the control still reads as ours. */
const SOCIAL_COLOR: Record<string, string> = {
  Facebook: "#1877F2",
  WhatsApp: "#25D366",
  X: "rgb(var(--text-primary))",
};

/** One footer column. */
function Col({
  title,
  links,
}: {
  title: string;
  links: { label: string; href: string }[];
}) {
  return (
    <div>
      <div className="label mb-3">{title}</div>
      <ul className="space-y-2">
        {links.map((l) => (
          <li key={l.href + l.label}>
            <Link
              href={l.href}
              className="text-xs text-muted hover:text-yellow"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter({ locale }: { locale: Locale }) {
  // Labels for the two answer-linking columns are the QUESTIONS themselves, pulled from the
  // translated FAQ by id. No parallel list of footer strings to translate, and a label can never
  // disagree with the answer it points at.
  const faqById = new Map(getFaq(locale).map((e) => [e.id, e.q]));
  const ask = (id: string) => ({
    label: faqById.get(id) ?? id,
    href: localePath(locale, `/faq#${id}`),
  });
  const socials = activeSocials();
  const wa = whatsappUrl();

  // Organization JSON-LD. `sameAs` is how a search or answer engine resolves the brand and its
  // social accounts to ONE entity — the reason those URLs must be right rather than plausible.
  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}#organization`,
    name: SITE_NAME,
    url: `${SITE_URL}${localeHome(locale)}`,
    inLanguage: LOCALE_TAG[locale],
    logo: `${SITE_URL}/tiger.svg`,
    description:
      "Non-custodial copy trading for crypto perpetual futures. Three automated master engines mirror their trades into the subscriber's own Bybit, Binance, MEXC or cTrader account, sized to their balance, with a mandatory stop-loss and take-profit on every position.",
    email: CONTACT_EMAIL,
    ...(socials.length > 0 ? { sameAs: socials.map((s) => s.url) } : {}),
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: CONTACT_EMAIL,
        availableLanguage: ["English"],
        ...(wa ? { url: wa } : {}),
      },
    ],
  };

  return (
    <footer
      lang={LOCALE_TAG[locale]}
      className="site-footer relative mt-20 w-full pt-10"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(org) }}
      />

      {/* The footer BAND is full-bleed; its CONTENT is re-constrained to the same column the
          pages use, so the text does not run to the screen edge on a wide monitor. */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6">

      <div className="relative grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        {/* Brand + how to reach a human */}
        <div>
          <Logo />
          <p className="mt-3 max-w-xs text-xs leading-relaxed text-muted">
            {t(locale, "foot.tagline")}
          </p>

          <div className="mt-5">
            <div className="label mb-2">{t(locale, "foot.talk")}</div>
            <ul className="space-y-2 text-xs">
              <li>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-muted hover:text-yellow"
                >
                  {CONTACT_EMAIL}
                </a>
              </li>
              {wa && (
                <li>
                  <a
                    href={wa}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-muted hover:text-yellow"
                  >
                    <span style={{ color: SOCIAL_COLOR.WhatsApp }}>
                      <IconWhatsApp />
                    </span>
                    {t(locale, "foot.whatsapp")}
                  </a>
                </li>
              )}
            </ul>
            <p className="mt-2 text-[11px] text-muted">
              {t(locale, "foot.neverSend")}
            </p>
          </div>

          {socials.length > 0 && (
            <div className="mt-5">
              <div className="label mb-2">{t(locale, "foot.follow")}</div>
              <div className="flex items-center gap-2">
                {socials.map((s) => {
                  const Icon = SOCIAL_ICON[s.name];
                  return (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer me"
                      aria-label={`twoke on ${s.name}`}
                      title={s.handle || s.name}
                      data-net={s.name}
                      className="hex social-chip flex h-8 w-8 items-center justify-center transition-colors hover:text-yellow!"
                      style={{ color: SOCIAL_COLOR[s.name] ?? "rgb(var(--text-muted))" }}
                    >
                      {Icon ? <Icon /> : s.name}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <Col
          title={t(locale, "foot.product")}
          links={[
            { label: t(locale, "foot.howItWorks"), href: localePath(locale, "/#how-it-works") },
            { label: t(locale, "foot.pricing"), href: localePath(locale, "/#pricing") },
            { label: t(locale, "nav.tutorials"), href: localePath(locale, "/tutorials") },
            { label: t(locale, "nav.faq"), href: localePath(locale, "/faq") },
            { label: t(locale, "nav.signIn"), href: "/login" },
            { label: t(locale, "foot.createAccount"), href: "/signup" },
          ]}
        />

        {/* Deep links into individual answers. Cheap internal linking, and it is how an engine
            learns that a specific question has a specific address on this site. */}
        <Col
          title={t(locale, "foot.learn")}
          links={[
            ask("what-is-copy-trading"),
            ask("what-is-twoke"),
            ask("how-a-trade-reaches-me"),
            ask("position-sizing"),
            ask("supported-exchanges"),
            ask("payment-methods"),
          ]}
        />

        <Col
          title={t(locale, "foot.trust")}
          links={[
            ask("does-twoke-hold-my-funds"),
            ask("mandatory-sl-tp"),
            ask("risk-settings"),
            ask("two-factor"),
            ask("export-my-data"),
            ask("refunds"),
            { label: t(locale, "foot.terms"), href: localePath(locale, "/legal/terms") },
            { label: t(locale, "foot.privacy"), href: localePath(locale, "/legal/privacy") },
          ]}
        />
      </div>

      {/* Risk warning. On a leveraged-futures product this belongs on every page, not only behind
          a modal at signup — and it is the paragraph a careless AI summary is most likely to drop,
          so it is stated plainly rather than in legalese. */}
      <div className="mt-10 border-t border-border pt-6">
        <p className="text-[11px] leading-relaxed text-muted">
          <b className="text-warn">{t(locale, "foot.riskTitle")}</b>{" "}
          {t(locale, "foot.risk")}
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pb-8 text-[11px] text-muted">
        <span>
          © {new Date().getFullYear()} {SITE_NAME} · non-custodial copy-trading
          · not financial advice
        </span>
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-1.5">
            <span className="status-dot h-1.5 w-1.5" /> Bybit, Binance, MEXC &amp; cTrader
          </span>
          <span className="flex items-center gap-1.5">
            <span className="status-dot-yellow status-dot h-1.5 w-1.5" /> USDC
            on Polygon
          </span>
          <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-primary">
            {CONTACT_EMAIL}
          </a>
        </span>
      </div>
      </div>
    </footer>
  );
}
